import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { DataSiswa } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"

const postData = async (newData: DataSiswa) => {
  const response = await api.post("/master/jalur/store", newData)
  return response.data
}

const patchData = async (newData: DataSiswa) => {
  const response = await api.patch("/master/jalur/update", newData)
  return response.data
}

const deleteData = async () => {
  const response = await api({
    url: "/master/jalur/delete",
    method: "DELETE",
    // data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: DataSiswa | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<DataSiswa>({
    id_data_siswa: selectedRow?.id_data_siswa || 0,
    id_user: selectedRow?.id_user || null,
    id_ref_sekolah: selectedRow?.id_ref_sekolah || null,
    id_ref_agama: selectedRow?.id_ref_agama || null,
    id_ref_wilayah: selectedRow?.id_ref_wilayah || null,
    id_ref_negara: selectedRow?.id_ref_negara || null,
    id_ref_jenis_tinggal: selectedRow?.id_ref_jenis_tinggal || null,
    id_ref_alat_transportasi: selectedRow?.id_ref_alat_transportasi || null,
    nik: selectedRow?.nik || "",
    nisn: selectedRow?.nisn || "",
    id_ref_jurusan_sekolah: selectedRow?.id_ref_jurusan_sekolah || null,
    nama_lengkap: selectedRow?.nama_lengkap || "",
    email: selectedRow?.email || "",
    jenis_kelamin: selectedRow?.jenis_kelamin || null,
    tempat_lahir: selectedRow?.tempat_lahir || "",
    tanggal_lahir: selectedRow?.tanggal_lahir || "",
    npwp: selectedRow?.npwp || "",
    nama_ibu_kandung: selectedRow?.nama_ibu_kandung || "",
    jalan: selectedRow?.jalan || "",
    dusun: selectedRow?.dusun || "",
    rt: selectedRow?.rt || null,
    rw: selectedRow?.rw || null,
    kelurahan: selectedRow?.kelurahan || "",
    kode_pos: selectedRow?.kode_pos || null,
    telepon: selectedRow?.telepon || "",
    handphone: selectedRow?.handphone || "",
    penerima_kps: selectedRow?.penerima_kps || null,
    nomor_kps: selectedRow?.nomor_kps || "",
    berat_badan: selectedRow?.berat_badan || null,
    tinggi_badan: selectedRow?.tinggi_badan || null,
    ukuran_baju: selectedRow?.ukuran_baju || null,
    path_foto: selectedRow?.path_foto || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJalur"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jalur"
          description={`Apakah anda yakin akan menghapus data jalur "${selectedRow?.nama_lengkap}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jalur" : "Update Data jalur"}
      />
      <div>
        <Label>Nama Lengkap</Label>
        <Input
          placeholder="Ketik nama jalur..."
          value={form.nama_lengkap || ""}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nama_lengkap: event.target.value }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
